import { useEffect } from "react";
import {
  Outlet,
  useMatch,
  useNavigate,
  generatePath,
  useParams,
  useLocation,
} from "react-router-dom";
import {
  RISK_ROUTE_CHARTS_MITIGATION_SEGMENT,
  RISK_ROUTE_CHARTS_OVERALL_SEGMENT,
  RISK_ROUTE_CHARTS_MAJOR_SEGMENT,
  RISK_ROUTE_CHARTS_MITIGATION,
  RISK_ROUTE_CHARTS_MAJOR,
} from "constants/routing";

import { AppliedConfigProvider } from "contexts/applied-config";
import { useGlobalFiltersContext } from "contexts/filters/global-filters";
import { useRiskManagementFiltersContext } from "contexts/filters/risk-management-filters";
import {
  getRiskChartsPath,
  parseWellIdNameParam,
  validateWellName,
} from "helpers/routing";
import type { RiskPathOption } from "helpers/routing";

export const RiskManagementCharts = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const riskMajorRouteMatch = useMatch({
    path: RISK_ROUTE_CHARTS_MAJOR,
    end: false,
  });

  const riskMitigationRouteMatch = useMatch({
    path: RISK_ROUTE_CHARTS_MITIGATION,
    end: false,
  });

  const params = useParams<{
    wellIdName?: string;
  }>();

  const {
    wellIdNameParam,
    setAppliedWell,
    filteredWellNamesByHierarchyUnfiltered,
  } = useGlobalFiltersContext();

  const {
    appliedRiskMitigationId,
    setChartsView,
    setAppliedRiskMitigationId,
    setSavedRiskMitigationId,
  } = useRiskManagementFiltersContext();

  let riskPathOption: RiskPathOption = RISK_ROUTE_CHARTS_OVERALL_SEGMENT;
  if (riskMajorRouteMatch) {
    riskPathOption = RISK_ROUTE_CHARTS_MAJOR_SEGMENT;
  } else if (riskMitigationRouteMatch && appliedRiskMitigationId) {
    riskPathOption = RISK_ROUTE_CHARTS_MITIGATION_SEGMENT;
  }

  useEffect(() => {
    // priority - url param, state, default as first well
    if (!filteredWellNamesByHierarchyUnfiltered.length) {
      return;
    }

    const unresolvedWellIdNameParam = params.wellIdName ?? wellIdNameParam;

    const [unresolvedHierarchyId, unresolvedWellName] = parseWellIdNameParam(
      unresolvedWellIdNameParam,
    );

    let [resolvedHierarchyId, resolvedWellName] = validateWellName(
      unresolvedHierarchyId,
      unresolvedWellName,
      filteredWellNamesByHierarchyUnfiltered,
    );

    // invalid well, set to default
    if (!resolvedWellName || resolvedHierarchyId === -1) {
      resolvedHierarchyId =
        filteredWellNamesByHierarchyUnfiltered[0].hierarchyId;
      resolvedWellName =
        filteredWellNamesByHierarchyUnfiltered[0].wellNames.at(0) ?? "";
    }

    // idempotent applied well change to avoid resetting selection unnecessarily
    // radio state change is kept in RiskAssetFilters as an effect to prevent reset of selected
    // to applied well when applied well doesn't change (e.g., navigating between tabs in RM)
    // w/o dependency in this useEffect
    setAppliedWell((current) =>
      current[0] === resolvedHierarchyId && current[1] === resolvedWellName
        ? current
        : [resolvedHierarchyId, resolvedWellName],
    );

    // update charts view
    setChartsView(riskPathOption);

    // reset mitigation
    // TODO: extend to mitigation id navigation once mitigation URL is refactored
    if (riskPathOption !== RISK_ROUTE_CHARTS_MITIGATION_SEGMENT) {
      setAppliedRiskMitigationId(0);
      setSavedRiskMitigationId(0);
    }

    // navigate
    const targetPath = generatePath(
      getRiskChartsPath(resolvedHierarchyId, resolvedWellName, riskPathOption),
    );

    if (location.pathname !== targetPath) {
      navigate(targetPath, {
        replace: true,
      });
    }
  }, [
    navigate,
    wellIdNameParam,
    riskPathOption,
    params.wellIdName,
    setAppliedWell,
    setChartsView,
    location.pathname,
    setAppliedRiskMitigationId,
    setSavedRiskMitigationId,
    filteredWellNamesByHierarchyUnfiltered,
  ]);

  return (
    <AppliedConfigProvider>
      <Outlet />
    </AppliedConfigProvider>
  );
};

export default RiskManagementCharts;
